// apps/frontend/src/components/common/AdminNoticeModal.tsx
// Modal qui pop-up quand l'user a des notices admin non vues.
// Dismissable via X (acknowledge cote backend). Une notice = un modal,
// les suivantes prennent le relais apres dismiss. L'user peut aussi
// repondre avec un texte + jusqu'a 5 pieces jointes (factures,
// certificats, captures...) — utile pour repondre a une notice
// "contrefacon" en envoyant les preuves d'authenticite.

import React, { useState } from 'react';
import {
  AlertTriangle,
  Info,
  ShieldAlert,
  X,
  ExternalLink,
  MessageSquare,
  Paperclip,
  Send,
  Loader2,
  Trash2,
} from 'lucide-react';
import {
  usePendingAdminNotices,
  type AdminNotice,
  type AdminNoticeSeverity,
} from '../../hooks/usePendingAdminNotices';
import { useAuth } from '../../hooks/useAuth';
import apiClient from '../../services/apiClient';

const SEVERITY_STYLES: Record<
  AdminNoticeSeverity,
  {
    Icon: React.FC<{ className?: string }>;
    iconWrapper: string;
    iconColor: string;
    headerBg: string;
    accent: string;
  }
> = {
  INFO: {
    Icon: Info,
    iconWrapper: 'bg-brand-cream border-brand-border',
    iconColor: 'text-brand-500',
    headerBg: 'bg-brand-offwhite',
    accent: 'from-[#C8A882] to-[#B89968]',
  },
  WARNING: {
    Icon: AlertTriangle,
    iconWrapper: 'bg-amber-50 border-amber-200',
    iconColor: 'text-amber-600',
    headerBg: 'bg-amber-50',
    accent: 'from-amber-500 to-amber-600',
  },
  CRITICAL: {
    Icon: ShieldAlert,
    iconWrapper: 'bg-rose-50 border-rose-200',
    iconColor: 'text-rose-600',
    headerBg: 'bg-rose-50',
    accent: 'from-rose-600 to-rose-700',
  },
};

const SEVERITY_BADGE: Record<AdminNoticeSeverity, string> = {
  INFO: 'Information',
  WARNING: 'Avertissement',
  CRITICAL: 'Important',
};

const MAX_ATTACHMENTS = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

const AdminNoticeModal: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { notices, acknowledge } = usePendingAdminNotices(isAuthenticated);

  const [replyMode, setReplyMode] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Une notice a la fois : on prend la plus recente (deja triee par
  // l'API). Le dismiss reduit la liste -> la suivante s'affiche.
  const current: AdminNotice | undefined = notices[0];
  if (!current) return null;

  const style = SEVERITY_STYLES[current.severity] ?? SEVERITY_STYLES.INFO;
  const Icon = style.Icon;

  const resetReply = () => {
    setReplyMode(false);
    setReplyBody('');
    setUploadedUrls([]);
    setError(null);
  };

  const handleClose = () => {
    resetReply();
    acknowledge(current.id);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // permet de re-selectionner le meme fichier
    if (files.length === 0) return;

    const remaining = MAX_ATTACHMENTS - uploadedUrls.length;
    if (remaining <= 0) {
      setError(`Maximum ${MAX_ATTACHMENTS} pièces jointes par réponse`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    setError(null);
    setUploading(true);
    try {
      for (const file of toUpload) {
        if (file.size > MAX_FILE_BYTES) {
          setError(
            `"${file.name}" dépasse 10 MB. Réduis la taille et réessaie.`
          );
          continue;
        }
        // On reutilise l'endpoint /upload?type=DISPUTE_EVIDENCE qui
        // accepte image/PDF/etc. via le middleware uploadProductImages.
        const formData = new FormData();
        formData.append('files', file);
        const token = localStorage.getItem('glowp_access_token');
        const resp = await fetch(
          `${(import.meta as any).env?.VITE_API_URL ?? ''}/api/v1/upload?type=DISPUTE_EVIDENCE`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );
        const data = await resp.json();
        if (data?.success) {
          const results = data.data?.results ?? data.results ?? [];
          for (const r of results) {
            if (r?.url) setUploadedUrls(prev => [...prev, r.url]);
          }
        } else {
          setError(data?.error || "Erreur d'upload");
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (url: string) => {
    setUploadedUrls(prev => prev.filter(u => u !== url));
  };

  const sendReply = async () => {
    if (!replyBody.trim()) {
      setError('Écris ton message avant d’envoyer.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const res = await apiClient.request(
        `/api/v1/notices/${current.id}/reply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            body: replyBody.trim(),
            attachments: uploadedUrls,
          }),
        }
      );
      if (!res.success) {
        throw new Error((res as any).error || "Erreur d'envoi");
      }
      // Apres reply, on acquitte la notice (sinon elle reste pending)
      resetReply();
      acknowledge(current.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur réseau');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-notice-title"
    >
      <div className="fixed inset-0 bg-black/55 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div
          className={`flex items-start gap-3 px-5 py-4 border-b border-brand-border ${style.headerBg}`}
        >
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${style.iconWrapper}`}
          >
            <Icon className={`w-5 h-5 ${style.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-brand-warm mb-0.5">
              {SEVERITY_BADGE[current.severity]} · Équipe Glowp
            </p>
            <h2
              id="admin-notice-title"
              className="text-base font-bold text-brand-dark leading-tight"
            >
              {current.title}
            </h2>
          </div>
          <button
            onClick={handleClose}
            aria-label="Fermer"
            className="w-9 h-9 rounded-full hover:bg-white/60 flex items-center justify-center text-brand-warm transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          <p className="text-sm text-brand-dark whitespace-pre-wrap leading-relaxed">
            {current.body}
          </p>

          {current.actionUrl && (
            <a
              href={current.actionUrl}
              target={
                current.actionUrl.startsWith('http') ? '_blank' : undefined
              }
              rel={
                current.actionUrl.startsWith('http')
                  ? 'noopener noreferrer'
                  : undefined
              }
              className={`mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r ${style.accent} text-white text-sm font-semibold hover:opacity-90 transition-opacity`}
            >
              {current.actionLabel ?? 'En savoir plus'}
              {current.actionUrl.startsWith('http') && (
                <ExternalLink className="w-3.5 h-3.5" />
              )}
            </a>
          )}

          {/* Bloc reponse */}
          {!replyMode ? (
            <button
              type="button"
              onClick={() => setReplyMode(true)}
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-500 hover:text-brand-warm"
            >
              <MessageSquare className="w-4 h-4" />
              Répondre à ce message
            </button>
          ) : (
            <div className="mt-5 pt-4 border-t border-brand-border space-y-3">
              <p className="text-xs font-semibold text-brand-dark">
                Ta réponse à l&apos;équipe Glowp
              </p>
              <textarea
                value={replyBody}
                onChange={e => setReplyBody(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 rounded-lg border border-brand-border focus:outline-none focus:border-brand-500 text-sm resize-none"
                placeholder="Écris ton message…"
              />

              {/* Attachments */}
              {uploadedUrls.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {uploadedUrls.map(url => {
                    const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(url);
                    return (
                      <div
                        key={url}
                        className="relative inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-brand-cream border border-brand-border text-[11px]"
                      >
                        {isImage ? (
                          <img
                            src={url}
                            alt=""
                            className="w-8 h-8 rounded object-cover"
                          />
                        ) : (
                          <Paperclip className="w-3 h-3" />
                        )}
                        <span className="text-brand-dark truncate max-w-[100px]">
                          {url.split('/').pop()}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(url)}
                          className="ml-1 text-brand-warm hover:text-rose-600"
                          aria-label="Retirer"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-warm cursor-pointer hover:text-brand-dark">
                  <Paperclip className="w-3.5 h-3.5" />
                  {uploading
                    ? 'Envoi…'
                    : `Ajouter une pièce jointe (${uploadedUrls.length}/${MAX_ATTACHMENTS})`}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    onChange={handleFileChange}
                    disabled={
                      uploading || uploadedUrls.length >= MAX_ATTACHMENTS
                    }
                    className="hidden"
                  />
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={resetReply}
                    className="text-xs px-3 py-1.5 rounded-lg border border-brand-border text-brand-dark hover:bg-brand-cream"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={sendReply}
                    disabled={sending || !replyBody.trim()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#C8A882] to-[#B89968] text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Envoi…
                      </>
                    ) : (
                      <>
                        <Send className="w-3.5 h-3.5" />
                        Envoyer
                      </>
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-brand-border bg-brand-offwhite text-center">
          <button
            onClick={handleClose}
            className="text-xs font-semibold text-brand-warm hover:text-brand-dark transition-colors"
          >
            J&apos;ai pris connaissance — fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminNoticeModal;
